import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import HeroVisual from "@/components/HeroVisual";
import HeroCTA from "@/components/HeroCTA";
import AmbientGlow from "@/components/AmbientGlow";
import { useScroll, useTransform } from "framer-motion";
import api from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const DEFAULT_HERO = {
    eyebrow: "Bikaner's First Coworking Space",
    line1: "Work Better.",
    line2: "Connect Better.",
    line3: "Create Better.",
    supporting: "A professional coworking space in Bikaner designed for focused work, collaboration and growth.",
};

const MaskedLine = ({ index, children, className = "" }) => (
    <span className="block overflow-hidden pb-[0.08em]">
        <motion.span
            custom={index}
            initial={{ y: "112%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.85, ease: EASE, delay: 0.1 + index * 0.1 }}
            className={`block ${className}`}
        >
            {children}
        </motion.span>
    </span>
);

export default function HeroSection() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const contentY = useTransform(scrollYProgress, [0, 1], [0, 70]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0.25]);
    const [hero, setHero] = useState(DEFAULT_HERO);

    useEffect(() => {
        api.get("/api/content/hero")
            .then((res) => {
                if (res.data && res.data.line1) setHero((h) => ({ ...h, ...res.data }));
            })
            .catch(() => {});
    }, []);

    return (
        <section ref={ref} id="top" className="relative overflow-hidden" data-testid="hero-section">
            {/* ambient glows — extremely restrained */}
            <AmbientGlow color="#7E35A8" side="right" opacity={0.07} className="-top-40 right-[-10%] h-[560px] w-[560px]" />
            <AmbientGlow color="#22B3A3" side="left" opacity={0.06} className="bottom-[-20%] left-[-8%] h-[480px] w-[480px]" />

            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-16 pt-32 sm:px-8 lg:min-h-[94svh] lg:grid-cols-[45%_55%] lg:gap-6 lg:pb-10 lg:pt-28">
                {/* LEFT — messaging */}
                <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}
                        className="inline-flex items-center gap-2.5 rounded-full border border-brand-purple/15 bg-white/70 px-4 py-2 shadow-[0_4px_16px_-8px_rgba(126,58,168,0.25)]"
                        data-testid="hero-eyebrow"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-brand-purple to-brand-teal" />
                        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink/60 sm:tracking-[0.24em]">
                            {hero.eyebrow}
                        </span>
                    </motion.div>

                    <h1 className="mt-7 text-[44px] font-bold leading-[1.06] tracking-[-0.01em] text-ink sm:text-6xl lg:text-[64px] xl:text-[72px]">
                        <MaskedLine index={0}>{hero.line1}</MaskedLine>
                        <MaskedLine index={1}>
                            <span className="text-gradient">{hero.line2}</span>
                        </MaskedLine>
                        <MaskedLine index={2}>{hero.line3}</MaskedLine>
                    </h1>

                    <motion.h2
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.48 }}
                        className="mt-6 text-base font-bold text-ink/80 md:text-lg"
                    >
                        {hero.supporting}
                    </motion.h2>

                    <div className="mt-8">
                        <HeroCTA />
                    </div>
                </motion.div>

                {/* RIGHT — animated workspace visualization */}
                <div id="workspace" className="relative">
                    <HeroVisual />
                </div>
            </div>
        </section>
    );
}
